import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Library, Upload, Send, Users, LogOut, Clapperboard, ShieldCheck, Pencil } from "lucide-react";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/library", label: "Asset Library", icon: Library, testId: "nav-library" },
  { to: "/import", label: "Import Video", icon: Upload, testId: "nav-import" },
  { to: "/publish", label: "Publishing", icon: Send, testId: "nav-publish" },
];

export default function AppLayout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-[#050505] text-white">
      <aside className="fixed left-0 top-0 z-30 flex h-screen w-64 flex-col border-r border-white/10 bg-[#050505] px-4 py-6">
        <div className="mb-10 flex items-center gap-2.5 px-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#00E5FF] text-black">
            <Clapperboard className="h-5 w-5" />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight">ReelForge</span>
        </div>

        <nav className="flex flex-1 flex-col gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-[#00E5FF]/10 text-[#00E5FF]" : "text-gray-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <l.icon className="h-[18px] w-[18px]" />
              {l.label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/team"
              data-testid="nav-team"
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-[#00E5FF]/10 text-[#00E5FF]" : "text-gray-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Users className="h-[18px] w-[18px]" />
              Team
            </NavLink>
          )}
        </nav>

        <div className="mt-auto border-t border-white/10 pt-4">
          <div className="mb-3 flex items-center gap-3 px-2" data-testid="sidebar-user">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1A1A1A] text-sm font-semibold text-[#00E5FF]">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{user?.name}</div>
              <div className="flex items-center gap-1 text-[11px] text-gray-500">
                {isAdmin ? <ShieldCheck className="h-3 w-3 text-[#00E5FF]" /> : <Pencil className="h-3 w-3 text-[#FF007F]" />}
                <span className="uppercase tracking-wide">{user?.role}</span>
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-gray-400 transition-colors hover:bg-white/5 hover:text-white"
          >
            <LogOut className="h-[18px] w-[18px]" /> Log out
          </button>
        </div>
      </aside>

      <main className="ml-64 flex-1">
        <Outlet />
      </main>
    </div>
  );
}
